import React, { FC } from 'react';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';
import { Button } from '@clearcover/atlas';
import urlJoin from 'url-join';
import { Link } from 'react-router-dom';

import { Column, FormInput, Row } from 'Shared/components';
import { formatPhoneNumber } from 'Shared/utils';
import { getContactInfoByFieldValue } from 'QuoteApp/utils';
import { RoutePrompt } from 'QuoteApp/components';
import { useAppContext } from 'QuoteApp/services/providers';
import { sanitizeString } from 'QuoteApp/services/endpoints/utils';
import { useQaToggleFeature } from 'Shared/hooks';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';

import { emailValidator } from './contactInfoValidation';
import styles from './styles.module.css';

type ContactInfoType = {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
};

export const ContactInfo: FC<ContactInfoType> = ({
  isEditing,
  setIsEditing,
}) => {
  const { details, setDetails, api, appId } = useAppContext();

  const contactInfoSchema = Yup.object().shape({
    contactInfo: Yup.object().shape({
      [details.principalInsuredId]: Yup.object().shape({
        email: emailValidator,
      }),
    }),
  });

  const isEmailEditable =
    details.drivers[details.principalInsuredId].email.editable;

  const [emailEditabilityFeatureFlag] = useQaToggleFeature(
    'agt-4374_purchase_screen_email_editability'
  );

  return (
    <Formik
      enableReinitialize
      initialValues={{
        contactInfo: getContactInfoByFieldValue(details.contactInfo, 'value'),
      }}
      validationSchema={contactInfoSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const response = await api.saveAppData({
            data: {
              insurableParties: [
                {
                  identifier: details.principalInsuredId,
                  contactInformation: {
                    email: sanitizeString(
                      values.contactInfo[details.principalInsuredId].email
                    ),
                  },
                },
              ],
            },
          });
          setDetails(response.data);
          setIsEditing(false);
        } catch {
          // api.saveAppData will throw a toast error
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        touched,
        errors,
        dirty,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
      }) => (
        <>
          <RoutePrompt
            when={dirty && !isSubmitting}
            editingIdDisplay=""
            isValid={isValid}
          />
          <h3 className={styles.contactInfoHeading}>Contact information</h3>
          {!emailEditabilityFeatureFlag && (
            <p>
              Please confirm the customer email for sending important post-bind
              documents. Clicking "Edit Email" will return you to the Summary
              page to make your changes.
            </p>
          )}
          <div className={styles.contactInfoForm}>
            <Row>
              <Column>
                <div className={styles.contactInfoLabel}>Phone</div>
                <span className="fs-mask">
                  {formatPhoneNumber(
                    details.contactInfo[details.principalInsuredId].phoneNumber
                      .value || ''
                  )}
                </span>
              </Column>
              <Column>
                <div className={styles.contactInfoLabel}>
                  Is this a mobile number?
                </div>
                {details.contactInfo[details.principalInsuredId].phoneType
                  .value === 'mobile'
                  ? 'Yes'
                  : 'No'}
              </Column>
            </Row>
            {emailEditabilityFeatureFlag ? (
              <Row className={styles.contactInfoEmailContainer}>
                <Column className={styles.contactInfoEmailInput}>
                  <FormInput
                    data-testid="email-address"
                    variant="text"
                    name={`contactInfo.${[details.principalInsuredId]}.email`}
                    value={values.contactInfo[details.principalInsuredId].email}
                    label="Email Address"
                    disabled={!isEditing}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={
                      getIn(
                        touched,
                        `contactInfo.${[details.principalInsuredId]}.email`
                      )
                        ? getIn(
                            errors,
                            `contactInfo.${[details.principalInsuredId]}.email`
                          )
                        : undefined
                    }
                  />
                </Column>
                <Column className={styles.contactInfoEmailButton}>
                  <Button
                    data-testid="purchase-edit-email"
                    size="md"
                    variant={isEditing ? 'primary' : 'secondary'}
                    type="button"
                    onClick={() => {
                      if (isEditing) {
                        handleSubmit();
                      } else {
                        setIsEditing(true);
                      }
                    }}
                    isSubmitting={isSubmitting}
                    disabled={!isEmailEditable}
                  >
                    {isEditing ? 'Save' : 'Edit'}
                  </Button>
                </Column>
              </Row>
            ) : (
              <Row className={styles.contactInfoEmailRow}>
                <Column>
                  <div className={styles.contactInfoLabel}>Email Address</div>
                  <span className="fs-mask">
                    {details.contactInfo[details.principalInsuredId].email
                      .value || ''}
                  </span>
                  <Link
                    className={styles.contactInfoEmailEditLink}
                    to={urlJoin(QUOTE_APP_ROUTE, appId, '/summary')}
                  >
                    Edit Email
                  </Link>
                </Column>
              </Row>
            )}
          </div>
        </>
      )}
    </Formik>
  );
};
